/**
 * FERP Portal - Auth Callback Page
 *
 * Handles OIDC callback after successful authentication.
 * AuthContext handles the actual token exchange via maybeHandleRedirectCallback().
 * This page just waits for auth state to update and redirects.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Center, Stack, Loader, Text, Alert } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useAuth } from '@/auth/AuthContext';

export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, isLoading, error: authError } = useAuth();

  useEffect(() => {
    // Check for OIDC error in URL
    const errorParam = searchParams.get('error');
    if (errorParam) {
      const errorDesc = searchParams.get('error_description') || 'Authentication failed';
      console.error('OIDC error:', errorParam, errorDesc);
      setError(errorDesc);
      return;
    }

    // If authenticated, redirect to dashboard
    if (isAuthenticated && !isLoading) {
      console.log('✅ Authenticated, redirecting to dashboard');
      navigate('/dashboard', { replace: true });
      return;
    }

    // If auth failed, show error
    if (authError && !isLoading) {
      console.error('Auth error:', authError);
      setError(authError);
    }
  }, [searchParams, navigate, isAuthenticated, isLoading, authError]);

  // Set a timeout to detect stuck state
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isAuthenticated && !error) {
        console.error('Auth callback timeout - no authentication after 10s');
        setError('Authentication timed out. Please try again.');
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, [isAuthenticated, error]);

  if (error) {
    return (
      <Center h="100vh">
        <Stack ta="center" gap="md" maw={400}>
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Authentication Failed"
            color="red"
            variant="light"
          >
            {error}
          </Alert>
          <Text
            size="sm"
            c="blue"
            style={{ cursor: 'pointer' }}
            onClick={() => navigate('/login', { replace: true })}
          >
            Return to login
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Center h="100vh">
      <Stack ta="center" gap="md">
        <Loader size="xl" type="dots" />
        <Text size="sm" c="dimmed">Completing authentication...</Text>
      </Stack>
    </Center>
  );
}
